import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  usePhotoControlEntries,
  usePhotoControlEntryDetail,
  useUpdatePhotoControlEntry,
} from '../hooks/usePhotoControl';
import { fetchAuthenticatedBlob } from '../../../api/client';
import type {
  PhotoControlEntryListItem,
  PhotoControlImageItem,
} from '../../../api/photo-control';
import { useAuth } from '../../auth/AuthProvider';
import { Button, Modal } from '../../../components/ui';
import { AuthenticatedVideo } from '../../../shared/components/AuthenticatedVideo';
import styles from './PhotoControlReviewPanel.module.scss';

type PhotoControlReviewPanelProps = {
  readonly patientId: string;
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Очікує',
  submitted: 'Надіслано',
  reviewed: 'Переглянуто',
};

const ANGLE_LABELS: Record<string, string> = {
  // Legacy values kept for old records.
  front: 'Фронт',
  upper: 'Зверху',
  lower: 'Знизу',
  right: 'Справа',
  left: 'Зліва',
  // New capture flow.
  front_closed: 'Центр, рот закритий',
  front_open: 'Центр, рот відкритий',
  lower_open: 'Знизу, рот відкритий',
  selfie_smile: 'Селфі з посмішкою',
  smile_zoom: 'Посмішка наближено',
  aligners_zoom: 'З елайнерами, наближено',
};

const STATUS_TABS = [
  { value: undefined, label: 'Усі' },
  { value: 'submitted', label: 'Надіслано' },
  { value: 'reviewed', label: 'Переглянуто' },
  { value: 'pending', label: 'Очікує' },
] as const;

function formatDate(value: string, locale: string): string {
  return new Date(`${value}T00:00:00`).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function PhotoControlReviewPanel({
  patientId,
}: PhotoControlReviewPanelProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);
  const [expandedEntryId, setExpandedEntryId] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState('');

  const entriesQuery = usePhotoControlEntries(patientId, { status: activeTab });
  const updateMutation = useUpdatePhotoControlEntry(patientId);

  const entries = entriesQuery.data ?? [];

  function handleExpand(entry: PhotoControlEntryListItem) {
    updateMutation.reset();
    if (expandedEntryId === entry.id) {
      setExpandedEntryId(null);
      return;
    }
    setExpandedEntryId(entry.id);
    setCommentDraft(entry.doctor_comment_preview ?? '');
  }

  function handleSaveComment(entryId: string) {
    updateMutation.mutate(
      { entryId, payload: { doctor_comment: commentDraft || null } },
      { onSuccess: () => setExpandedEntryId(null) },
    );
  }

  function handleMarkReviewed(entryId: string) {
    updateMutation.mutate(
      {
        entryId,
        payload: { status: 'reviewed', doctor_comment: commentDraft || null },
      },
      { onSuccess: () => setExpandedEntryId(null) },
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div>
          <h4 className={styles.title}>
            {t('clientDetail.photoControl.review.title')}
          </h4>
          <p className={styles.subtitle}>
            {t('clientDetail.photoControl.review.subtitle')}
          </p>
        </div>
        {!entriesQuery.isLoading && (
          <span className={styles.countBadge}>{entries.length}</span>
        )}
      </div>

      <div
        className={styles.tabs}
        role="group"
        aria-label={t('clientDetail.monitoring.filterByStatus')}
      >
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.label}
            type="button"
            className={`${styles.tab} ${activeTab === tab.value ? styles.active : ''}`}
            onClick={() => setActiveTab(tab.value)}
            aria-pressed={activeTab === tab.value}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {entriesQuery.isLoading ? (
        <div
          className={styles.loadingList}
          aria-label={t('clientDetail.monitoring.loadingEntries')}
        >
          <span />
          <span />
          <span />
        </div>
      ) : entriesQuery.isError ? (
        <p className={styles.errorState} role="alert">
          {t('clientDetail.monitoring.loadEntriesFailed')}
        </p>
      ) : entries.length === 0 ? (
        <p className={styles.empty}>Записів не знайдено</p>
      ) : (
        <div className={styles.entryList}>
          {entries.map((entry) => (
            <EntryCard
              key={entry.id}
              entry={entry}
              patientId={patientId}
              isExpanded={expandedEntryId === entry.id}
              commentDraft={commentDraft}
              onToggleExpand={() => handleExpand(entry)}
              onCommentChange={setCommentDraft}
              onSaveComment={() => handleSaveComment(entry.id)}
              onMarkReviewed={() => handleMarkReviewed(entry.id)}
              isSaving={updateMutation.isPending}
              hasSaveError={updateMutation.isError}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Entry card ───────────────────────────────────────────────────────────────

type EntryCardProps = {
  readonly entry: PhotoControlEntryListItem;
  readonly patientId: string;
  readonly isExpanded: boolean;
  readonly commentDraft: string;
  readonly onToggleExpand: () => void;
  readonly onCommentChange: (value: string) => void;
  readonly onSaveComment: () => void;
  readonly onMarkReviewed: () => void;
  readonly isSaving: boolean;
  readonly hasSaveError: boolean;
};

function EntryCard({
  entry,
  patientId,
  isExpanded,
  commentDraft,
  onToggleExpand,
  onCommentChange,
  onSaveComment,
  onMarkReviewed,
  isSaving,
  hasSaveError,
}: EntryCardProps) {
  const { t, i18n } = useTranslation();
  return (
    <article
      className={`${styles.entryCard} ${isExpanded ? styles.expanded : ''}`}
    >
      <button
        type="button"
        className={styles.entryHeader}
        onClick={onToggleExpand}
        aria-expanded={isExpanded}
        aria-controls={`photo-entry-${entry.id}`}
      >
        <div className={styles.entrySummary}>
          <span className={styles.dueDate}>
            {formatDate(entry.due_date, i18n.language)}
          </span>
          {entry.cycle_number != null && (
            <span className={styles.imageCount}>
              {t('clientDetail.monitoring.stageN', {
                number: entry.cycle_number,
              })}
            </span>
          )}
          <span className={styles.imageCount}>
            {t('clientDetail.photoControl.review.photoCount', {
              count: entry.image_count,
            })}
          </span>
        </div>
        <div className={styles.statusGroup}>
          {entry.trigger_source === 'wearing_stage_entry' && (
            <span className={styles.triggerBadge}>
              {t(
                'clientDetail.photoControl.triggerSource.wearingStageEntry',
                'Авто · початок носіння',
              )}
            </span>
          )}
          <span className={`${styles.statusBadge} ${styles[entry.status]}`}>
            {STATUS_LABELS[entry.status] ?? entry.status}
          </span>
          <span className={styles.chevron} aria-hidden="true">
            ⌄
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className={styles.entryBody} id={`photo-entry-${entry.id}`}>
          <EntryMedia patientId={patientId} entryId={entry.id} />

          <div className={styles.commentSection}>
            <div
              id={`photo-comment-label-${entry.id}`}
              className={styles.commentLabel}
            >
              {t('clientDetail.photoControl.review.doctorComment')}
            </div>
            {entry.status === 'reviewed' ? (
              <div className={styles.commentText}>
                {entry.doctor_comment_preview ||
                  t('clientDetail.photoControl.review.noComment')}
              </div>
            ) : (
              <textarea
                id={`photo-comment-${entry.id}`}
                aria-labelledby={`photo-comment-label-${entry.id}`}
                className={styles.commentInput}
                value={commentDraft}
                onChange={(e) => onCommentChange(e.target.value)}
                placeholder="Додати коментар для пацієнта..."
              />
            )}
          </div>

          {entry.status !== 'reviewed' && (
            <div className={styles.actionRow}>
              <Button
                variant="secondary"
                size="sm"
                onClick={onSaveComment}
                disabled={isSaving}
              >
                Зберегти коментар
              </Button>
              {entry.status === 'submitted' && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onMarkReviewed}
                  disabled={isSaving}
                >
                  Позначити як переглянуто
                </Button>
              )}
            </div>
          )}
          {hasSaveError && (
            <p className={styles.errorState} role="alert">
              {t('clientDetail.photoControl.review.saveFailed')}
            </p>
          )}
        </div>
      )}
    </article>
  );
}

// ─── Entry media (photos + video) ─────────────────────────────────────────────

type EntryMediaProps = {
  readonly patientId: string;
  readonly entryId: string;
};

function EntryMedia({ patientId, entryId }: EntryMediaProps) {
  const { tokens } = useAuth();
  const token = tokens?.access_token ?? '';
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [videoOpen, setVideoOpen] = useState(false);

  const detailQuery = usePhotoControlEntryDetail(patientId, entryId);

  if (detailQuery.isLoading) {
    return (
      <div className={styles.imageGrid}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className={`${styles.thumbnail} ${styles.thumbnailSkeleton}`}
          />
        ))}
      </div>
    );
  }

  if (!detailQuery.data) return null;

  const { images, video_submitted } = detailQuery.data;
  const videoPath = `/admin/patients/${patientId}/photo-controls/${entryId}/video/content`;
  const hasMedia = images.length > 0 || video_submitted;

  return (
    <>
      {hasMedia ? (
        <div className={styles.imageGrid}>
          {images.map((img, idx) => (
            <AuthenticatedImage
              key={img.id}
              path={`/admin/patients/${patientId}/photo-controls/${entryId}/photos/${img.id}/content`}
              token={token}
              alt={ANGLE_LABELS[img.angle_type] ?? img.angle_type}
              label={ANGLE_LABELS[img.angle_type] ?? img.angle_type}
              onClick={() => setLightboxIndex(idx)}
              className={styles.thumbnail}
            />
          ))}
          {video_submitted && (
            <VideoThumbnail
              path={videoPath}
              token={token}
              onClick={() => setVideoOpen(true)}
            />
          )}
        </div>
      ) : (
        <div className={styles.thumbnailPlaceholder}>Немає фото</div>
      )}

      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          patientId={patientId}
          entryId={entryId}
          token={token}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {videoOpen && (
        <VideoLightbox
          path={videoPath}
          token={token}
          onClose={() => setVideoOpen(false)}
        />
      )}
    </>
  );
}

// ─── Video thumbnail (first frame as poster) ──────────────────────────────────

type VideoThumbnailProps = {
  readonly path: string;
  readonly token: string;
  readonly onClick: () => void;
};

function VideoThumbnail({ path, token, onClick }: VideoThumbnailProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!token) return;
    let objUrl: string | null = null;
    let cancelled = false;

    setHasError(false);
    fetchAuthenticatedBlob(path, token)
      .then((blob) => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(blob);
        setBlobUrl(objUrl);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      });

    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [path, token]);

  function handleLoadedMetadata() {
    const video = videoRef.current;
    if (!video) return;
    // Seek a touch past 0 so a real frame paints instead of a black poster.
    try {
      video.currentTime = Math.min(0.1, (video.duration || 1) / 2);
    } catch {
      /* seeking unsupported — fall back to the gradient + play badge */
    }
  }

  return (
    <div className={styles.thumbnailWrapper}>
      <button
        type="button"
        className={`${styles.thumbnailBtn} ${styles.videoThumb}`}
        onClick={onClick}
        title="Відтворити відео"
      >
        {blobUrl && !hasError && (
          <video
            ref={videoRef}
            className={styles.videoThumbVideo}
            src={blobUrl}
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={handleLoadedMetadata}
          />
        )}
        <span className={styles.videoThumbPlay} aria-hidden="true">
          ▶
        </span>
      </button>
      <span className={styles.thumbnailLabel}>Відео</span>
    </div>
  );
}

// ─── Video lightbox (fullscreen playback) ─────────────────────────────────────

type VideoLightboxProps = {
  readonly path: string;
  readonly token: string;
  readonly onClose: () => void;
};

function VideoLightbox({ path, token, onClose }: VideoLightboxProps) {
  const { t } = useTranslation();

  return (
    <Modal
      open
      onClose={onClose}
      title={t('clientDetail.photoControl.review.viewVideo')}
      closeAriaLabel={t('clientDetail.photoControl.review.closeVideo')}
      size="xl"
    >
      <div className={styles.videoLightboxContent}>
        <AuthenticatedVideo path={path} token={token} fullscreen autoPlay />
      </div>
    </Modal>
  );
}

// ─── Authenticated image ──────────────────────────────────────────────────────

type AuthenticatedImageProps = {
  readonly path: string;
  readonly token: string;
  readonly alt: string;
  readonly label?: string;
  readonly onClick?: () => void;
  readonly className?: string;
};

function AuthenticatedImage({
  path,
  token,
  alt,
  label,
  onClick,
  className,
}: AuthenticatedImageProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!token) return;
    let objUrl: string | null = null;
    let cancelled = false;

    setIsLoading(true);
    setHasError(false);

    fetchAuthenticatedBlob(path, token)
      .then((blob) => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(blob);
        setBlobUrl(objUrl);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [path, token]);

  if (isLoading) {
    return <div className={`${className ?? ''} ${styles.thumbnailSkeleton}`} />;
  }

  if (hasError || !blobUrl) {
    return (
      <div
        className={`${className ?? ''} ${styles.thumbnailError}`}
        title={alt}
      >
        !
      </div>
    );
  }

  const inner = <img src={blobUrl} alt={alt} className={className} />;

  if (onClick) {
    return (
      <div className={styles.thumbnailWrapper}>
        <button
          type="button"
          className={styles.thumbnailBtn}
          onClick={onClick}
          title={label}
        >
          {inner}
        </button>
        {label && <span className={styles.thumbnailLabel}>{label}</span>}
      </div>
    );
  }

  return inner;
}

// ─── Lightbox ─────────────────────────────────────────────────────────────────

type LightboxProps = {
  readonly images: readonly PhotoControlImageItem[];
  readonly patientId: string;
  readonly entryId: string;
  readonly token: string;
  readonly initialIndex: number;
  readonly onClose: () => void;
};

function Lightbox({
  images,
  patientId,
  entryId,
  token,
  initialIndex,
  onClose,
}: LightboxProps) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(initialIndex);
  const current = images[index];

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight')
        setIndex((i) => Math.min(images.length - 1, i + 1));
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [images.length]);

  if (!current) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={t('clientDetail.photoControl.review.viewPhotos')}
      closeAriaLabel={t('clientDetail.photoControl.review.closePhotos')}
      size="xl"
      footer={
        <div className={styles.lightboxFooter}>
          <button
            type="button"
            className={styles.lightboxNav}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={index === 0}
            aria-label={t('clientDetail.photoControl.review.previousPhoto')}
          >
            ‹
          </button>
          <span className={styles.lightboxCaption}>
            {ANGLE_LABELS[current.angle_type] ?? current.angle_type}{' '}
            <span className={styles.lightboxCounter}>
              {index + 1} / {images.length}
            </span>
          </span>
          <button
            type="button"
            className={styles.lightboxNav}
            onClick={() => setIndex((i) => Math.min(images.length - 1, i + 1))}
            disabled={index === images.length - 1}
            aria-label={t('clientDetail.photoControl.review.nextPhoto')}
          >
            ›
          </button>
        </div>
      }
    >
      <div className={styles.lightboxImageWrap}>
        <AuthenticatedImage
          path={`/admin/patients/${patientId}/photo-controls/${entryId}/photos/${current.id}/content`}
          token={token}
          alt={ANGLE_LABELS[current.angle_type] ?? current.angle_type}
          className={styles.lightboxImage}
        />
      </div>
    </Modal>
  );
}
